import { useFormContext } from 'react-hook-form';
import { array } from 'fp-ts';
import { SettlementRuleAction, SettlementRuleType, SettlementValueType } from '@mews-commander/data-access';
import { FormInputField, FormRadioGroupField, LocalizedEnum, lt } from '@mews/framework';
import { Banner, RadioButton, Stack } from '@optimus-web/core';
import { CommanderFormProps, useHandledDialogFormControls } from '@mews-commander/form';
import { TextKey } from '@mews-commander/enums';
import { getCurrentEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { SettlementRuleFormField, WithPaymentPolicyId } from '../types';
import { useSettlementRuleGetSuspenseQuery, useSettlementRuleMappedEntitiesSuspenseQuery } from '../queries';
import { PaymentPolicyTimingFields } from './PaymentPolicyTimingFields';
import { FormValues, resolver } from './schema';
import { SettlementRuleValueField } from './SettlementRuleValueField';
import { MessageWithEntitiesCount } from './MessageWithEntitiesCount';
import { useGetInitialFormValues } from './useGetInitialFormValues';

interface CommonFormContentProps extends CommanderFormProps {
    onSubmit: (values: FormValues) => Promise<unknown>;
}

interface PaymentPolicyFormContentProps {
    disableValueDiscriminator?: boolean;
    defaultCurrencyCode: string;
}

export const PaymentPolicyFormContent = ({
    disableValueDiscriminator,
    defaultCurrencyCode,
}: PaymentPolicyFormContentProps) => {
    const { clearErrors } = useFormContext<FormValues>();
    const handleActionChange = () => clearErrors(`${SettlementRuleFormField.Value}.value.value`);

    return (
        <Stack vertical spacing="300">
            <FormInputField autoFocus name={SettlementRuleFormField.Name} label={lt('Name')} />
            <FormInputField
                name={SettlementRuleFormField.PaymentPolicyKey}
                label={lt('PaymentPolicyKeyLabel')}
                helper={lt('PaymentPolicyKeyHelper')}
            />
            <SettlementRuleValueField
                defaultCurrencyCode={defaultCurrencyCode}
                disableValueDiscriminator={disableValueDiscriminator}
            />
            <PaymentPolicyTimingFields />
            <FormRadioGroupField
                name={SettlementRuleFormField.Action}
                label={lt('Method')}
                onChange={handleActionChange}
                required
            >
                {Object.values(SettlementRuleAction).map((action) => (
                    <RadioButton
                        label={<LocalizedEnum prefix="SettlementRuleAction" value={action} />}
                        value={action}
                        key={action}
                    />
                ))}
            </FormRadioGroupField>
            <FormRadioGroupField name={SettlementRuleFormField.Type} label={lt('SettlementRuleMode')} required>
                {Object.values(SettlementRuleType).map((type) => (
                    <RadioButton
                        label={<LocalizedEnum prefix="SettlementRuleType" value={type} />}
                        value={type}
                        key={type}
                    />
                ))}
            </FormRadioGroupField>
        </Stack>
    );
};

export const CreatePaymentPolicyFormContent = (props: CommonFormContentProps) => {
    const {
        defaultValues,
        formValues,
        formValues: {
            value: { discriminator, value },
        },
    } = useGetInitialFormValues();
    const enterpriseDefaultCurrencyCode: string = useCommanderSelector(getCurrentEnterpriseDefaultCurrencyCode);

    const { renderFormContent } = useHandledDialogFormControls({
        ...props,
        defaultValues,
        values: formValues,
        resolver,
    });

    return renderFormContent(
        <PaymentPolicyFormContent
            defaultCurrencyCode={
                (discriminator === SettlementValueType.Flat && value.currencyCode) || enterpriseDefaultCurrencyCode
            }
        />
    );
};

export const UpdatePaymentPolicyFormContent = ({
    paymentPolicyId,
    ...formControls
}: CommonFormContentProps & WithPaymentPolicyId) => {
    const paymentPolicyQuery = useSettlementRuleGetSuspenseQuery(paymentPolicyId);
    const paymentPolicyEntitiesQuery = useSettlementRuleMappedEntitiesSuspenseQuery({
        id: paymentPolicyId,
    });

    const {
        defaultValues,
        formValues,
        formValues: {
            value: { discriminator, value },
        },
    } = useGetInitialFormValues(paymentPolicyQuery.data);

    const enterpriseDefaultCurrencyCode: string = useCommanderSelector(getCurrentEnterpriseDefaultCurrencyCode);

    const { renderFormContent } = useHandledDialogFormControls({
        ...formControls,
        defaultValues,
        values: formValues,
        resolver,
    });

    const isAssignedToEntities =
        array.isNonEmpty(paymentPolicyEntitiesQuery.data.rateGroups) ||
        array.isNonEmpty(paymentPolicyEntitiesQuery.data.rates);

    return renderFormContent(
        <Stack vertical distribution="space-between" spacing="300">
            <Banner
                variant="info"
                primaryText={lt('PaymentPolicyEditBannerTitle')}
                secondaryText={
                    isAssignedToEntities ? (
                        <MessageWithEntitiesCount
                            textKey={TextKey.PaymentPolicyEditBannerDescription}
                            data={paymentPolicyEntitiesQuery.data}
                        />
                    ) : null
                }
            />
            <PaymentPolicyFormContent
                disableValueDiscriminator
                defaultCurrencyCode={
                    (discriminator === SettlementValueType.Flat && value.currencyCode) || enterpriseDefaultCurrencyCode
                }
            />
        </Stack>
    );
};
